import React from 'react';
import SignIn from './components/SignInForm';
import Layout from '../../components/Layout';
import { SplitPageLayout } from '../../types/Layout';
import CoverImage from '../../components/CoverImage';
import PageMeta from '../../components/PageMeta';
import { useTranslation } from 'react-i18next';
import { useAuthProvider } from '../../providers/Auth';
import { SessionState } from '../../providers/Auth/utils/types';
import { Redirect } from 'react-router';
import { useConfig } from '../../providers/Config';
import { useMediaQuery, Theme } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useLogin } from './utils/login';
import { cleanEmail } from '../../utils';
import { SignInFormValues } from './types';
import { useHistory } from 'react-router-dom';

const SignInPage: React.FC = () => {
  const history = useHistory<{ to: string }>();
  const { t } = useTranslation();
  const { sessionState } = useAuthProvider();
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<SignInFormValues>();
  const { handleLogin, loading } = useLogin();
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const {
    tenantName,
    tenantDisplay: {
      logos: { logo },
      coverImages,
    },
    tenantBot: { botName },
    featureFlags,
  } = useConfig();
  const { disableRegistration, enableRegistrationWithToken } = featureFlags || {};

  const successPath = history.location.state?.to;

  const layout: SplitPageLayout = {
    flexDirection: 'row-reverse',
    main: (
      <SignIn
        tenantName={tenantName}
        logo={logo}
        isSmallDevice={isSmallDevice}
        errors={errors}
        control={control}
        handleSubmit={handleSubmit(async (data) => {
          const loginSuccessful = await handleLogin(cleanEmail(data.email), data.password);
          if (loginSuccessful) {
            // shows the login success message later when the user eventually
            // gets to their homepage e.g. for service users this is the '/app' page (chat bot)
            // and for everyone else it's the /app/tasks page
            history.push('/app/verify-number', { to: successPath });
          }
        })}
        botName={botName}
        t={t}
        hideCreateAccount={disableRegistration || enableRegistrationWithToken ? true : false}
        loading={loading}
      />
    ),
    right: <CoverImage imageSrc={coverImages} />,
  };

  // If the user is already signed in then redirect them to the app
  if (SessionState[sessionState] === 'valid' && !loading) return <Redirect to={'/app'} />;
  else if (!['valid', 'pendingRehydrate'].includes(SessionState[sessionState]))
    return (
      <>
        <PageMeta title={t('ns.sign_in:page_title')} />
        <Layout showHeader={false} layout={layout} showFooter={'half'} />
      </>
    );
  else return null;
};

export default SignInPage;
